import { useEffect, useRef, useState } from "react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext.tsx";
import { SSOAuthButton } from "./SSOAuthButton";
import {
  disableEmailPasswordAuthentication,
  googleWorkplaceDomain,
} from "./authConfig";
import { AuthBrandPanel } from "./AuthBrandPanel";

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/loginpage LoginPage documentation}
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/security Security documentation}
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const { darkModeLogo, title } = useConfigurationContext();
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const hasDisplayedRecoveryNotification = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldNotify = searchParams.get("passwordRecoveryEmailSent") === "1";

    if (!shouldNotify || hasDisplayedRecoveryNotification.current) {
      return;
    }

    hasDisplayedRecoveryNotification.current = true;
    notify("crm.auth.recovery_email_sent", {
      type: "success",
      messageArgs: {
        _: "If you're a registered user, you should receive a password recovery email shortly.",
      },
    });

    searchParams.delete("passwordRecoveryEmailSent");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  return (
    <div className="min-h-screen bg-[#f7f0e7] lg:flex">
      <AuthBrandPanel logo={darkModeLogo} title={title} />
      <div className="flex min-h-screen w-full items-center justify-center p-5 sm:p-8 lg:w-[54%]">
        <div className="w-full max-w-[390px] rounded-[1.7rem] border border-[#eadfd2] bg-card p-7 shadow-[0_18px_40px_rgba(65,48,36,0.1)] sm:p-9">
          <div className="mb-8">
            <div className="mb-5 flex items-center gap-2 lg:hidden">
              <img
                className="size-9 rounded-lg object-contain"
                src={darkModeLogo}
                alt={title}
              />
              <span className="font-semibold tracking-[-0.025em]">{title}</span>
            </div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#b65338]">
              Welcome back
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-[-0.045em]">
              {translate("ra.auth.sign_in")}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Continue where your customer story left off.
            </p>
          </div>
          {disableEmailPasswordAuthentication ? null : (
            <Form className="space-y-8" onSubmit={handleSubmit}>
              <TextInput
                label="ra.auth.email"
                source="email"
                type="email"
                validate={required()}
              />
              <TextInput
                label="ra.auth.password"
                source="password"
                type="password"
                validate={required()}
              />
              <div className="flex flex-col gap-4 pt-1">
                <Button
                  type="submit"
                  className="cursor-pointer rounded-xl bg-[#d76f4a] hover:bg-[#bd5839]"
                  disabled={loading}
                >
                  {translate("ra.auth.sign_in")}
                </Button>
              </div>
            </Form>
          )}
          {googleWorkplaceDomain ? (
            <SSOAuthButton className="w-full" domain={googleWorkplaceDomain}>
              {translate("crm.auth.sign_in_google_workspace", {
                _: "Sign in with Google Workplace",
              })}
            </SSOAuthButton>
          ) : null}
          {disableEmailPasswordAuthentication ? null : (
            <Link
              to={"/forgot-password"}
              className="block text-sm text-center hover:underline"
            >
              {translate("ra-supabase.auth.forgot_password", {
                _: "Forgot password?",
              })}
            </Link>
          )}
        </div>
      </div>
      <Notification />
    </div>
  );
};
